import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import BlockEditorPanel from './BlockEditorPanel';
import { parseBlocks, serializeBlocks } from '../services/block-utils';
import { fetchBufferRange } from '../services/api';
import { saveWithRetry, splitTimeMark } from '../services/save-utils';
import { outboxGetForRead } from '../services/outbox';
import { isSent, getSentTarget } from '../services/inbox-utils';
import useBlockSwipe, { DRAG_MAX, COMMIT_RATIO } from '../hooks/useBlockSwipe';

/**
 * Format a date string for display (local time).
 * Returns a simple formatted date string — no "Today"/"Yesterday" labels.
 */
function displayDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const opts = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
  return date.toLocaleDateString('en-US', opts);
}

/** Return today's date string in YYYY-MM-DD format (local time). */
function localTodayStr() {
  return new Date().toLocaleDateString('sv-SE');
}

/** Return tomorrow's date string in YYYY-MM-DD format (local time). */
function localTomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString('sv-SE');
}

/**
 * TimelineNode — continuous-scroll view for a folder of date-based .md files.
 *
 * All daily files are shown together in a single scrollable column, newest
 * first (today at the top).  Each date renders as a section with its own
 * header + block cards.  Older dates load dynamically as the user scrolls
 * toward the bottom, and a fixed header always shows the active date.
 *
 * Swipe gestures (bidirectional, per block):
 *   Swipe left  → enter move mode (select target node in sidebar)
 *   Swipe right → open options panel (delete + future options)
 */
export default function TimelineNode({
  folderPath,
  moveMode,
  swipeEnabled = true,
  onMoveBlock,
  onDateChange,
  toolbar,
  onSwipeProgress,
  onSwipeCommit,
  onSwipeCancel,
  onOpenOptions,
  nodeList,
  onSendToNode,
  onBlockChange,
  blockAutoSaveWord = 'over',
  blockAutoSaveDelay = 3,
  destinations,
}) {

  const { toast } = useOutletContext();

  // ── Continuous scroll state ──────────────────────────────────
  const [entries, setEntries] = useState([]);
  // [{ date, filePath, blocks: string[], sha: string|null, version: number|null }] — newest first
  const [activeDate, setActiveDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);  // { message } | null
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // ── Continuous scroll constants ────────────────────────────
  const TARGET_BUFFER = 15;
  const LOW_WATER = 5;
  const MAX_ENTRIES = 30;
  const KEEP_ENTRIES = 20;

  // ── Continuous scroll refs ─────────────────────────────────
  const isLoadingOlderRef = useRef(false);
  const scrollTimerRef = useRef(null);

  // ── Block edit modal state ──────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);   // { filePath, blockIndex }
  const [modalInitial, setModalInitial] = useState('');

  // ── Notify parent when the active date changes ──────────────
  useEffect(() => {
    if (!onDateChange) return;
    const entry = entries.find(e => e.date === activeDate);
    if (entry) onDateChange(entry.filePath, entry.date);
  }, [activeDate, entries, onDateChange]);

  // ── Convert API response entry → local state ────────────────
  const entryFromApi = useCallback(async (e) => {
    if (!e || !e.exists) return null;
    // Outbox-first: a pending local edit is the file's latest content.
    const ob = await outboxGetForRead(e.file_path);
    const content = ob ? ob.content : e.content;
    const sha = ob ? (ob.baseSha ?? null) : e.sha;
    return {
      date: e.date,
      filePath: e.file_path,
      blocks: parseBlocks(content || ''),
      sha: sha || null,
      version: e.version ?? null,
    };
  }, []);

  // ── Count prepared blocks below the viewport ────────────────
  const countPreparedBlocks = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return TARGET_BUFFER; // not mounted yet — don't trigger
    const viewportBottom = el.scrollTop + el.clientHeight;
    const containerTop = el.getBoundingClientRect().top;
    let count = 0;
    el.querySelectorAll('.v2-block-card').forEach(card => {
      const rect = card.getBoundingClientRect();
      if (rect.top - containerTop >= viewportBottom - 10) count++;
    });
    return count;
  }, []);

  // ── Load older dates (dynamic loading) ──────────────────────
  const loadOlderDates = useCallback(async () => {
    if (isLoadingOlderRef.current || !hasMoreOlder) return;
    isLoadingOlderRef.current = true;
    setLoadingOlder(true);
    setLoadError(null);
    try {
      const avgBlocksPerDate = entries.reduce((sum, e) => sum + e.blocks.length, 0)
                             / Math.max(1, entries.length);
      const preparedCount = countPreparedBlocks();
      const needBlocks = TARGET_BUFFER - preparedCount;
      const limit = Math.min(10, Math.max(3, Math.ceil(needBlocks / Math.max(1, avgBlocksPerDate))));

      const oldestDate = entries.length > 0
        ? entries[entries.length - 1].date
        : localTodayStr();
      const result = await fetchBufferRange(folderPath, oldestDate, 'before', limit);

      const existingDates = new Set(entries.map(e => e.date));
      const newEntries = [];
      for (const e of result.entries) {
        if (e.exists && !existingDates.has(e.date)) {
          const mapped = await entryFromApi(e);
          if (mapped) newEntries.push(mapped);
        }
      }

      if (newEntries.length > 0) {
        setEntries(prev => {
          const merged = [...prev, ...newEntries];
          return merged.length > MAX_ENTRIES ? merged.slice(0, KEEP_ENTRIES) : merged;
        });
      }
      setHasMoreOlder(result.has_more);
    } catch (err) {
      console.error('TimelineNode: failed to load older:', err);
      setLoadError(err.message || 'Failed to load');
    } finally {
      setLoadingOlder(false);
      isLoadingOlderRef.current = false;
    }
  }, [entries, folderPath, hasMoreOlder, entryFromApi, countPreparedBlocks]);

  // ── Initial load ─────────────────────────────────────────────
  const todayStr = localTodayStr();
  const todayRef = useRef(todayStr);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Use tomorrow as cursor so today is included in the results.
        // direction='before' returns dates strictly before the cursor.
        const tomorrow = localTomorrowStr();
        const result = await fetchBufferRange(folderPath, tomorrow, 'before', 5);
        if (cancelled) return;

        const mapped = [];
        for (const e of result.entries) {
          if (e.exists) {
            const m = await entryFromApi(e);
            if (m) mapped.push(m);
          }
        }

        // Today might not have a file yet — prepend a virtual entry
        // so the user can write immediately (sha: null → creates on save).
        const todayIdx = mapped.findIndex(e => e.date === todayStr);
        if (todayIdx === -1) {
          // A block may have been moved/copied into today's file just before
          // this view opened — it sits in the outbox until sync, and the
          // server's folder list doesn't know about it yet.  Read outbox-first
          // so it appears immediately instead of rendering an empty day.
          const todayPath = `${folderPath}/${todayStr}.md`;
          const ob = await outboxGetForRead(todayPath);
          mapped.unshift({
            date: todayStr,
            filePath: todayPath,
            blocks: ob ? parseBlocks(ob.content || '') : [],
            sha: ob ? (ob.baseSha ?? null) : null,
            version: null,
          });
        } else if (todayIdx > 0) {
          const [today] = mapped.splice(todayIdx, 1);
          mapped.unshift(today);
        }

        setEntries(mapped);
        setActiveDate(todayStr);
        setHasMoreOlder(result.has_more);
      } catch (err) {
        if (!cancelled) {
          console.error('TimelineNode: failed initial load:', err);
          setError({ message: err.message || 'Failed to load timeline' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [folderPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Midnight rollover detection ──────────────────────────────
  // When the user stays on the page past local midnight, the initial
  // load effect won't re-fire because [folderPath] is stable.
  // Poll every 60 s and prepend today's virtual entry on date change.
  // Also check immediately on visibility change (tab regains focus)
  // since browsers throttle setInterval in background tabs.
  useEffect(() => {
    let intervalId;
    const check = async () => {
      const current = localTodayStr();
      if (current !== todayRef.current) {
        todayRef.current = current;
        // Same as initial load — a just-written today's file may only exist
        // in the outbox, so read it there to avoid showing an empty day.
        const filePath = `${folderPath}/${current}.md`;
        const ob = await outboxGetForRead(filePath);
        setEntries(prev => {
          if (prev[0]?.date === current) return prev;
          return [{
            date: current,
            filePath,
            blocks: ob ? parseBlocks(ob.content || '') : [],
            sha: ob ? (ob.baseSha ?? null) : null,
            version: null,
          }, ...prev];
        });
        setActiveDate(current);
      }
    };
    // Reset interval on visibility change so the timer is fresh
    const onVisible = () => {
      check();
      clearInterval(intervalId);
      intervalId = setInterval(check, 60_000);
    };
    document.addEventListener('visibilitychange', onVisible);
    intervalId = setInterval(check, 60_000);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [folderPath]);

  // ── Scroll → debounced preload check ────────────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        if (isLoadingOlderRef.current || !hasMoreOlder) return;
        if (countPreparedBlocks() < LOW_WATER) {
          loadOlderDates();
        }
      }, 200);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [hasMoreOlder, countPreparedBlocks, loadOlderDates]);

  // ── Active date tracking (IntersectionObserver) ─────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (observed) => {
        const visible = observed
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveDate(visible[0].target.dataset.date);
        }
      },
      {
        root: el,
        rootMargin: '0px 0px -85% 0px',
        threshold: 0,
      }
    );

    // Observe all date headers.
    const observeHeaders = () => {
      el.querySelectorAll('.tl-date-header').forEach(h => observer.observe(h));
    };
    observeHeaders();
    // Re-observe when entries change (new date sections added).
    const mutationObserver = new MutationObserver(() => {
      el.querySelectorAll('.tl-date-header').forEach(h => {
        try { observer.unobserve(h); } catch (_) { /* may not be observed yet */ }
        observer.observe(h);
      });
    });
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [entries]);

  // ── Fire-and-forget save helper ───────────────────────────
  // Write-ahead: the save is queued in the outbox and pushed in the
  // background — the backend sha only changes later, so no mirror/sha
  // update here.
  const saveAndMirror = useCallback((filePath, content, sha, version) => {
    saveWithRetry(filePath, content, sha, version, toast, false)
      .catch(() => {}); // error handled by saveWithRetry (toast)
  }, [toast]);

  // ── Block CRUD (modal-based) ──────────────────────────────────
  const openNewBlock = () => {
    const targetDate = activeDate || entries[0]?.date;
    if (!targetDate) return;
    const entry = entries.find(e => e.date === targetDate);
    if (!entry) return;
    setEditingFile({ filePath: entry.filePath, blockIndex: null });
    setModalInitial('');
    setModalOpen(true);
  };

  const openEditBlock = (filePath, blockIndex, content) => {
    setEditingFile({ filePath, blockIndex });
    setModalInitial(content);
    setModalOpen(true);
  };

  const handleBlockConfirm = (content) => {
    if (!editingFile) return;
    const { filePath, blockIndex } = editingFile;

    const entry = entries.find(e => e.filePath === filePath);
    const currentBlocks = entry ? entry.blocks : [];
    let updatedBlocks;
    if (blockIndex === null) {
      updatedBlocks = [content, ...currentBlocks];
    } else {
      updatedBlocks = [...currentBlocks];
      updatedBlocks[blockIndex] = content;
    }
    const newContent = serializeBlocks(updatedBlocks);

    saveAndMirror(filePath, newContent, entry?.sha || null, null);

    setEntries(prev => prev.map(e =>
      e.filePath === filePath ? { ...e, blocks: updatedBlocks } : e
    ));

    onBlockChange?.();
    setModalOpen(false);
    setEditingFile(null);
  };

  // ── Modal move-to-destination: save content, close modal, then move ──
  const handleMoveToDestination = useCallback((dest, blockText) => {
    if (!editingFile || !blockText) return;
    const { filePath, blockIndex } = editingFile;

    const entry = entries.find(e => e.filePath === filePath);
    const srcIndex = blockIndex !== null ? blockIndex : 0;
    const srcContent = blockText;

    // For node destinations: remove block from source before saving.
    // For 'nodes' destination: keep block (enters move mode in parent).
    const isMoveToTarget = dest.type === 'node';

    const currentBlocks = entry ? entry.blocks : [];
    let updatedBlocks;
    if (blockIndex === null) {
      updatedBlocks = [blockText, ...currentBlocks];
    } else {
      updatedBlocks = [...currentBlocks];
      updatedBlocks[blockIndex] = blockText;
    }

    // Remove from source when moving to a direct target
    if (isMoveToTarget && srcIndex < updatedBlocks.length) {
      updatedBlocks.splice(srcIndex, 1);
    }

    const newContent = serializeBlocks(updatedBlocks);

    saveAndMirror(filePath, newContent, entry?.sha || null, null);

    setEntries(prev => prev.map(e =>
      e.filePath === filePath ? { ...e, blocks: updatedBlocks } : e
    ));

    // Close modal first
    setModalOpen(false);
    setEditingFile(null);
    onBlockChange?.();

    // 2. Route to destination
    if (dest.type === 'nodes') {
      onMoveBlock(filePath, srcIndex, srcContent);
    } else if (dest.type === 'node' && dest.path) {
      onSendToNode?.(filePath, srcIndex, srcContent, dest.path, { skipSourceRemoval: true });
    }
  }, [editingFile, entries, saveAndMirror, onBlockChange, onMoveBlock, onSendToNode]);

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingFile(null);
  };

  const handleDeleteBlock = () => {
    if (!editingFile) return;
    const { filePath, blockIndex } = editingFile;
    if (blockIndex === null) return; // Can't delete a new block

    const entry = entries.find(e => e.filePath === filePath);
    if (!entry) return;
    const updatedBlocks = [...entry.blocks];
    updatedBlocks.splice(blockIndex, 1);
    const newContent = serializeBlocks(updatedBlocks);
    saveAndMirror(filePath, newContent, entry.sha, null);

    setEntries(prev => prev.map(e =>
      e.filePath === filePath ? { ...e, blocks: updatedBlocks } : e
    ));

    onBlockChange?.();
    setModalOpen(false);
    setEditingFile(null);
  };

  // ── Move block ──────────────────────────────────────────────
  const handleMoveBlock = (filePath, blockIndex) => {
    const entry = entries.find(e => e.filePath === filePath);
    if (!entry) return;
    const content = entry.blocks[blockIndex];
    if (content !== undefined) {
      onMoveBlock(filePath, blockIndex, content);
    }
  };

  // ── Bidirectional swipe hook ────────────────────────────────
  const contentRefs = useRef({}); // index → content DOM element
  const cardRefs = useRef({});    // index → card DOM element
  const scrollRef = useRef(null); // scroll container
  const swipeEntryRef = useRef(null); // entry being swiped (multi-day)

  const handleSwipeCommitWrapped = useCallback((direction, index) => {
    const entry = swipeEntryRef.current;
    if (!entry) return;
    const content = entry.blocks[index];
    if (content !== undefined) {
      if (onSwipeCommit) {
        onSwipeCommit(direction, entry.filePath, index, content);
      } else {
        onMoveBlock(entry.filePath, index, content);
      }
    }
  }, [onSwipeCommit, onMoveBlock]);

  const handleSwipeCancelWrapped = useCallback(() => {
    if (onSwipeCancel) onSwipeCancel();
  }, [onSwipeCancel]);

  const { touchHandlers, mouseHandlers, swipedIndex, swipeProgress, swipeJustCommitted, closeSwipe } = useBlockSwipe({
    enabled: swipeEnabled,
    moveMode,
    contentRefs,
    cardRefs,
    enableReorder: false,
    blockCount: 0,
    scrollContainerRef: scrollRef,
    onSwipeProgress: onSwipeProgress,
    onSwipeCommit: handleSwipeCommitWrapped,
    onSwipeCancel: handleSwipeCancelWrapped,
  });

  // ── Render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="v2-right-empty">
        <span className="spinner" />
        <div>Loading timeline...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v2-right-empty">
        <div className="v2-right-empty-icon">&#x26A0;</div>
        <div style={{ fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-xs)' }}>
          Failed to load timeline
        </div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)', maxWidth: 320, textAlign: 'center' }}>
          {error.message}
        </div>
        <div style={{ marginTop: 'var(--space-md)', display: 'flex', gap: 'var(--space-sm)' }}>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh page
          </button>
        </div>
      </div>
    );
  }

  const activeEntry = entries.find(e => e.date === activeDate) || entries[0];

  // Shared props for the block editor — passed to BlockEditorPanel
  // and the desktop modal so the two render paths never drift apart.
  const blockEditProps = {
    show: modalOpen,
    initialContent: modalInitial,
    placeholder: editingFile?.blockIndex === null ? 'Write something...' : undefined,
    onConfirm: handleBlockConfirm,
    onClose: handleModalClose,
    onDelete: handleDeleteBlock,
    destinations,
    onMoveToDestination: handleMoveToDestination,
    autoSaveWord: blockAutoSaveWord,
    autoSaveDelay: blockAutoSaveDelay * 1000,
    filePath: editingFile?.filePath || '',
  };

  return (
    <>
      <div className="tl-view-continuous">
        {/* ── Fixed top bar — always shows active date + quick-add ── */}
        <div className="tl-fixed-header">
          <span>{activeEntry ? displayDate(activeEntry.date) : 'Timeline'}</span>
          {!moveMode && (
            <button
              className="tl-fixed-add-btn"
              onClick={openNewBlock}
              title="New block"
              aria-label="New block"
            >+ New</button>
          )}
        </div>

        {/* ── Single-scrollbar container ─────────────────────── */}
        <div className="tl-scroll-container scroll-y" ref={scrollRef}>
          {entries.map((entry) => {
            const isActive = entry.date === activeDate;

            return (
              <div className="tl-date-section" key={entry.date}>
                <div className="tl-date-header" data-date={entry.date}>
                  <span>{displayDate(entry.date)}</span>
                  <span className="tl-count">{entry.blocks.length}</span>
                </div>

                {/* Toolbar (active date only) */}
                {isActive && toolbar && (
                  <div className="v2-block-toolbar">
                    <div style={{ flex: 1 }} />
                    {toolbar}
                  </div>
                )}

                {/* Blocks */}
                {entry.blocks.length === 0 ? (
                  <div className="tl-empty-day">
                    <div className="tl-empty-day-icon">💭</div>
                    <div>Nothing captured on this day.</div>
                  </div>
                ) : (
                  entry.blocks.map((content, idx) => {
                    // ── Sent marker ──────────────────────────────
                    const sent = isSent(content);
                    const sentTarget = sent ? getSentTarget(content) : null;

                    // A leading time record is shown as a badge above the body.
                    const { badge, body } = splitTimeMark(content);

                    // ── Edge-strip state for this card ──────────────
                    const strip = (swipeProgress
                      && swipeEntryRef.current?.date === entry.date
                      && swipeProgress.index === idx)
                      ? swipeProgress
                      : null;
                    const stripWidth = strip ? Math.round(strip.ratio * DRAG_MAX) : 0;
                    const stripCommitted = strip && strip.ratio >= COMMIT_RATIO;
                    const stripBg = stripCommitted
                      ? 'rgba(var(--accent-rgb), 0.25)'
                      : 'rgba(128, 128, 128, 0.15)';

                    return (
                    <Fragment key={idx}>
                      <div
                        className="v2-block-card"
                        ref={(el) => { cardRefs.current[idx] = el; }}
                        onClick={(e) => {
                          if (moveMode) return;
                          // Suppress click after a swipe/drag — the swipe already handled the action
                          if (swipeJustCommitted.current) {
                            swipeJustCommitted.current = false;
                            return;
                          }
                          if (swipedIndex === idx && swipeEntryRef.current?.date === entry.date) {
                            closeSwipe();
                            return;
                          }
                          openEditBlock(entry.filePath, idx, content);
                        }}
                        onTouchStart={(e) => {
                          swipeEntryRef.current = entry;
                          touchHandlers.onTouchStart(e, idx);
                        }}
                        onTouchMove={(e) => touchHandlers.onTouchMove(e, idx)}
                        onTouchEnd={(e) => touchHandlers.onTouchEnd(e, idx)}
                        onMouseDown={(e) => {
                          swipeEntryRef.current = entry;
                          mouseHandlers.onMouseDown(e, idx);
                        }}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => {
                          if (moveMode) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openEditBlock(entry.filePath, idx, content);
                          }
                        }}
                      >
                        {/* ── Swipe progress edge strips ──────────── */}
                        {strip && strip.direction === 'left' && (
                          <div
                            className="v2-swipe-strip"
                            data-side="left"
                            style={{
                              left: 0,
                              width: stripWidth,
                              background: stripBg,
                            }}
                          />
                        )}
                        {strip && strip.direction === 'right' && (
                          <div
                            className="v2-swipe-strip"
                            data-side="right"
                            style={{
                              right: 0,
                              width: stripWidth,
                              background: stripBg,
                            }}
                          />
                        )}

                        {/* Content */}
                        <div
                          className="v2-block-card-content"
                          ref={(el) => { contentRefs.current[idx] = el; }}
                        >
                          {badge && <div className="v2-block-time-badge">{badge}</div>}
                          {body}
                          {sent && sentTarget && (
                            <div className="v2-block-sent-badge" title={`Sent to ${sentTarget}`}>
                              &#x2713; {sentTarget.split('/').pop().replace(/\.md$/, '')}
                            </div>
                          )}
                        </div>

                      </div>
                    </Fragment>
                    );
                  })
                )}
              </div>
            );
          })}

          {/* Bottom sentinel */}
          {hasMoreOlder && (
            <div className="tl-loading-sentinel">
              {loadingOlder && <span className="spinner" />}
              {!loadingOlder && loadError && (
                <button onClick={loadOlderDates}>⚠ Load failed — tap to retry</button>
              )}
              {!loadingOlder && !loadError && <span>⟳ Loading more...</span>}
            </div>
          )}
          {!hasMoreOlder && entries.length > 0 && (
            <div className="tl-loading-sentinel tl-sentinel-end">
              — No earlier records —
            </div>
          )}
        </div>
      </div>

      {/* ── Block edit — bottom panel on all platforms ── */}
      <BlockEditorPanel {...blockEditProps} />
    </>
  );
}
